import { useState } from 'react';
import { Button, Card, Icon, IconButton, Tag } from '../ds';
import { Eyebrow } from '../components/primitives';
import { Field, TextInput } from './PrepScreen';
import * as api from '../lib/db';
import { canCreateFamily } from '../lib/access';
import { useAuth } from '../state/AuthContext';
import { useFamily } from '../state/FamilyContext';
import { toISODate } from '../lib/time';
import type { Child, ParentRole } from '../lib/types';

/**
 * First run.
 *
 * Three ways in: start a new family, join your partner's with their code, or
 * arrive on a grandparent share link. Names, roles and children all go into
 * Firestore here — nothing about the family is hardcoded in the app.
 */

type Mode = 'choose' | 'create' | 'join' | 'share';

type ChildDraft = {
  name: string;
  nameEn: string;
  kind: Child['kind'];
  sex: Child['sex'];
  birthDate: string;
};

const ROLE_DEFAULTS: Record<ParentRole, { name: string; nameEn: string; initial: string }> = {
  mom: { name: 'ママ', nameEn: 'Mom', initial: 'マ' },
  dad: { name: 'パパ', nameEn: 'Dad', initial: 'パ' },
};

export function SetupScreen({ shareCode }: { shareCode?: string | null }) {
  const { user, signOut } = useAuth();
  const { refreshUser } = useFamily();

  const [mode, setMode] = useState<Mode>(shareCode ? 'share' : 'choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (task: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await task();
      refreshUser();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'うまくいきませんでした。もう一度おためしください。');
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div
      className="scroll-area"
      style={{
        minHeight: '100dvh',
        maxWidth: 520,
        margin: '0 auto',
        padding: '26px 20px calc(32px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        {mode !== 'choose' && !shareCode ? (
          <IconButton icon="ChevronLeft" label="もどる" variant="ghost" onClick={() => setMode('choose')} />
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow>Setup · はじめの設定</Eyebrow>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'var(--text-xl)',
              margin: '2px 0 0',
            }}
          >
            {mode === 'create'
              ? '家族をつくる'
              : mode === 'join'
                ? '家族に参加する'
                : mode === 'share'
                  ? 'アルバムを見る'
                  : 'ようこそ'}
          </h1>
        </div>
      </div>

      {mode === 'choose' ? (
        <>
          {canCreateFamily(user) ? (
            <Card padding="md" interactive onClick={() => setMode('create')} style={{ marginBottom: 10 }}>
              <Row icon="Plus" title="新しくはじめる" subtitle="家族と子どもを登録します" />
            </Card>
          ) : null}
          <Card padding="md" interactive onClick={() => setMode('join')}>
            <Row icon="UserPlus" title="招待コードで参加" subtitle="パートナーからもらったコードを入力" />
          </Card>
        </>
      ) : null}

      {mode === 'create' && canCreateFamily(user) ? (
        <CreateForm busy={busy} onSubmit={(input) => run(() => api.createFamily(user, input))} />
      ) : null}

      {mode === 'join' ? (
        <JoinForm
          busy={busy}
          onSubmit={(code, profile) => run(() => api.joinFamilyByInvite(user, code, profile))}
        />
      ) : null}

      {mode === 'share' && shareCode ? (
        <ShareJoinForm
          busy={busy}
          defaultName={user.displayName ?? ''}
          onSubmit={(profile) => run(() => api.joinFamilyByShare(user, shareCode, profile))}
        />
      ) : null}

      {error ? (
        <div
          className="cjk"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)', marginTop: 14 }}
        >
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => void signOut()}
        style={{
          display: 'block',
          margin: '28px auto 0',
          border: 'none',
          background: 'transparent',
          color: 'var(--text-subtle)',
          fontSize: 'var(--text-xs)',
          cursor: 'pointer',
        }}
      >
        別のアカウントでサインイン
      </button>
    </div>
  );
}

function Row({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span
        style={{
          flex: 'none',
          width: 38,
          height: 38,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--brand-subtle)',
          color: 'var(--brand)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={19} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--text-md)' }}>{title}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{subtitle}</div>
      </div>
      <Icon name="ChevronRight" size={18} />
    </div>
  );
}

function RolePicker({ value, onChange }: { value: ParentRole; onChange: (r: ParentRole) => void }) {
  return (
    <div style={{ display: 'flex', gap: 7, marginBottom: 14 }}>
      {(['mom', 'dad'] as ParentRole[]).map((role) => (
        <Tag key={role} selected={value === role} onClick={() => onChange(role)}>
          {ROLE_DEFAULTS[role].name}
        </Tag>
      ))}
    </div>
  );
}

function CreateForm({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (input: api.CreateFamilyInput) => void;
}) {
  const [familyName, setFamilyName] = useState('');
  const [role, setRole] = useState<ParentRole>('mom');
  const [name, setName] = useState(ROLE_DEFAULTS.mom.name);
  const [kids, setKids] = useState<ChildDraft[]>([
    { name: '', nameEn: '', kind: 'baby', sex: 'unspecified', birthDate: toISODate(new Date()) },
  ]);

  const pickRole = (next: ParentRole) => {
    // Only overwrite the name if it is still the untouched default.
    setRole(next);
    if (name === ROLE_DEFAULTS[role].name || !name.trim()) setName(ROLE_DEFAULTS[next].name);
  };

  const valid = kids.some((k) => k.name.trim()) && name.trim();

  return (
    <>
      <Card padding="md" style={{ marginBottom: 12 }}>
        <Field label="家族の名前 · Family" hint="例：たなか家">
          <TextInput value={familyName} onChange={setFamilyName} placeholder="わたしたちの家" />
        </Field>

        <Eyebrow style={{ marginBottom: 6 }}>あなたは · You</Eyebrow>
        <RolePicker value={role} onChange={pickRole} />
        <Field label="呼びかた · Display name">
          <TextInput value={name} onChange={setName} placeholder={ROLE_DEFAULTS[role].name} />
        </Field>
      </Card>

      <Eyebrow style={{ marginBottom: 8 }}>お子さん · Children</Eyebrow>
      {kids.map((kid, i) => (
        <Card key={i} padding="md" style={{ marginBottom: 10 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <div style={{ display: 'flex', gap: 7 }}>
              {(['baby', 'kid'] as Child['kind'][]).map((k) => (
                <Tag
                  key={k}
                  selected={kid.kind === k}
                  onClick={() => setKids((all) => all.map((c, j) => (j === i ? { ...c, kind: k } : c)))}
                >
                  {k === 'baby' ? '赤ちゃん' : '園児'}
                </Tag>
              ))}
            </div>
            {kids.length > 1 ? (
              <IconButton
                icon="Trash2"
                label="このお子さんを消す"
                variant="ghost"
                size="sm"
                onClick={() => setKids((all) => all.filter((_, j) => j !== i))}
              />
            ) : null}
          </div>

          <Field label="なまえ · Name">
            <TextInput
              value={kid.name}
              onChange={(v) => setKids((all) => all.map((c, j) => (j === i ? { ...c, name: v } : c)))}
              placeholder="はると"
            />
          </Field>
          <Field label="English">
            <TextInput
              value={kid.nameEn}
              onChange={(v) => setKids((all) => all.map((c, j) => (j === i ? { ...c, nameEn: v } : c)))}
              placeholder="Haruto"
            />
          </Field>

          <Eyebrow style={{ marginBottom: 6 }}>誕生日 · Birthday</Eyebrow>
          <input
            type="date"
            value={kid.birthDate}
            max={toISODate(new Date())}
            onChange={(e) =>
              setKids((all) => all.map((c, j) => (j === i ? { ...c, birthDate: e.target.value } : c)))
            }
            aria-label="誕生日"
            style={{
              width: '100%',
              height: 'var(--control-md)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontFamily: 'var(--font-mono)',
              marginBottom: 14,
            }}
          />

          <Eyebrow style={{ marginBottom: 6 }}>性別 · Sex</Eyebrow>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 7 }}>
            WHOの成長曲線は男女で別なので、正しく表示するために使います。
          </div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {(
              [
                ['male', '男の子'],
                ['female', '女の子'],
                ['unspecified', '未設定'],
              ] as [Child['sex'], string][]
            ).map(([value, label]) => (
              <Tag
                key={value}
                selected={kid.sex === value}
                onClick={() => setKids((all) => all.map((c, j) => (j === i ? { ...c, sex: value } : c)))}
              >
                {label}
              </Tag>
            ))}
          </div>
        </Card>
      ))}

      <Button
        variant="ghost"
        fullWidth
        iconStart="Plus"
        style={{ marginBottom: 16 }}
        onClick={() =>
          setKids((all) => [
            ...all,
            { name: '', nameEn: '', kind: 'kid', sex: 'unspecified', birthDate: toISODate(new Date()) },
          ])
        }
      >
        お子さんを追加
      </Button>

      <Button
        variant="primary"
        fullWidth
        size="lg"
        iconStart="Check"
        disabled={!valid || busy}
        onClick={() =>
          onSubmit({
            familyName: familyName.trim() || 'わたしたちの家',
            role,
            name: name.trim(),
            nameEn: ROLE_DEFAULTS[role].nameEn,
            initial: name.trim().slice(0, 1) || ROLE_DEFAULTS[role].initial,
            children: kids
              .filter((k) => k.name.trim())
              .map((k) => ({ ...k, name: k.name.trim(), nameEn: k.nameEn.trim() })),
          })
        }
      >
        {busy ? '作成中…' : 'はじめる · Start'}
      </Button>
    </>
  );
}

function JoinForm({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (code: string, profile: { role: ParentRole; name: string; nameEn: string; initial: string }) => void;
}) {
  const [code, setCode] = useState('');
  const [role, setRole] = useState<ParentRole>('dad');
  const [name, setName] = useState(ROLE_DEFAULTS.dad.name);

  return (
    <Card padding="md">
      <Field label="招待コード · Invite code" hint="パートナーの設定画面に表示されています">
        <TextInput value={code} onChange={(v) => setCode(v.toUpperCase())} placeholder="ABC123" autoFocus />
      </Field>

      <Eyebrow style={{ marginBottom: 6 }}>あなたは · You</Eyebrow>
      <RolePicker
        value={role}
        onChange={(next) => {
          setRole(next);
          if (name === ROLE_DEFAULTS[role].name || !name.trim()) setName(ROLE_DEFAULTS[next].name);
        }}
      />
      <Field label="呼びかた · Display name">
        <TextInput value={name} onChange={setName} placeholder={ROLE_DEFAULTS[role].name} />
      </Field>

      <Button
        variant="primary"
        fullWidth
        size="lg"
        iconStart="Check"
        disabled={code.trim().length < 4 || !name.trim() || busy}
        onClick={() =>
          onSubmit(code, {
            role,
            name: name.trim(),
            nameEn: ROLE_DEFAULTS[role].nameEn,
            initial: name.trim().slice(0, 1) || ROLE_DEFAULTS[role].initial,
          })
        }
      >
        {busy ? '参加中…' : '参加する · Join'}
      </Button>
    </Card>
  );
}

function ShareJoinForm({
  busy,
  defaultName,
  onSubmit,
}: {
  busy: boolean;
  defaultName: string;
  onSubmit: (profile: { name: string; nameEn: string; initial: string }) => void;
}) {
  const [name, setName] = useState(defaultName);

  return (
    <Card padding="md">
      <p className="cjk" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 16 }}>
        共有アルバムに招待されています。写真を見るだけで、記録は変更できません。
      </p>
      <Field label="お名前 · Your name" hint="家族の画面にこの名前が表示されます">
        <TextInput value={name} onChange={setName} placeholder="おばあちゃん" autoFocus />
      </Field>
      <Button
        variant="primary"
        fullWidth
        size="lg"
        iconStart="Eye"
        disabled={!name.trim() || busy}
        onClick={() =>
          onSubmit({ name: name.trim(), nameEn: name.trim(), initial: name.trim().slice(0, 1) || '？' })
        }
      >
        {busy ? '参加中…' : 'アルバムを見る'}
      </Button>
    </Card>
  );
}
